import { isPlatformBrowser } from '@angular/common';
import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';

import { catchError, throwError } from 'rxjs';

import { ModalService } from '@services/components/ModalService/modal.service';
import { SERVER_ERROR_MODAL, TOO_MANY_REQUESTS_MODAL } from '@shared/constants/modal.constants';
import { isServerError, isTooManyRequestsError } from '@shared/utils/http-error.utils';

const ADMIN_ROUTE_PREFIX = '/administracion';

/**
 * Checkout and payment requests keep their own 5xx handling: after a server error the order
 * may or may not exist, and those screens explain what to do in each case.
 */
const OWN_SERVER_ERROR_HANDLING_ENDPOINTS: readonly RegExp[] = [
  /\/api\/orders(\?|$)/,
  /\/api\/orders\/[^/]+\/payment\//,
];

function hasOwnServerErrorHandling(req: HttpRequest<unknown>, currentUrl: string): boolean {
  return currentUrl.startsWith(ADMIN_ROUTE_PREFIX)
    || OWN_SERVER_ERROR_HANDLING_ENDPOINTS.some((endpoint) => endpoint.test(req.url));
}

/**
 * Shows one shared modal for rate limiting (429) and server errors (5xx) so each screen does not
 * need its own message. The error is always rethrown so callers can still reset their state.
 */
export const httpErrorFeedbackInterceptor: HttpInterceptorFn = (req, next) => {
  const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  const modalService = inject(ModalService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (isBrowser && isTooManyRequestsError(error)) {
        modalService.openModal(TOO_MANY_REQUESTS_MODAL);
      } else if (isBrowser && isServerError(error) && !hasOwnServerErrorHandling(req, router.url)) {
        modalService.openModal(SERVER_ERROR_MODAL);
      }

      return throwError(() => error);
    }),
  );
};
